"use client"
import { MangoBlockProps } from "@/types/general";
import SectionTitle from "./shared/Title/SectionTitle";
import useMenus from "@pulsex/hooks/src/lib/queries/useMenus";
import pulseConfig from "@/pulsex.config";
import { cn } from "@pulsex/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface TitleWidgetType extends MangoBlockProps {
    isSticky?: boolean;
}

export default function TitleWidget({
    block,
    isSticky = true,
    ...rest
}: TitleWidgetType) {
    const { menus } = useMenus(pulseConfig);
    const pathname = usePathname();
    const submenus = menus?.getMenuByKey("h-submenus");
    const videoTVTabs = submenus?.find(
        (menu) => menu?.title?.toLowerCase() === "tv"
    );
    const isTVPage = videoTVTabs?.subMenus?.some((tab) => pathname === tab.url);

    // for pages that display programs, we want to add the word 'TV' to the page title,
    // in order to determine if the current page displays programs, we can check if
    // there is a submenu with title = 'tv' and if the current path has a dynamic param
    // eg /videos/[param]
    const isProgramsPage =
        pathname.startsWith("/videos/") &&
        !pathname.includes("documentaries") &&
        videoTVTabs !== null;

    let widget = (rest as any)?.widget;
    if (!block && !(rest as any)?.widget) return;
    if (block) widget = block.getMainWidget();
    else widget = (rest as any)?.widget;
    if (!widget) return;
    
    // if the pathname fits the patterns /news/* and without any more slashes (so /news/economy/something is not allowed))
    const isNewsCategoriesPage = pathname && pathname.startsWith('/news/') && pathname.split('/').length === 3;

    return (
        <div
            className={`my-2 lg:mb-2 w-full ${
                isSticky ? "sticky top-[195px] lg:top-36" : ""
            } pt-3 bg-white dark:bg-darkbg z-30`}
        >
            <div className="container mx-auto">
                {isNewsCategoriesPage && <CategoriesMenu />}
                <div className="flex items-center justify-between pb-2 w-full border-b-[3px] border-blue dark:border-white">
                    <SectionTitle
                        className="text-4xl font-black text-blue !mb-0"
                        title={
                            isProgramsPage
                                ? `TV - ${widget?.title}`
                                : widget?.title?.replace("#", "")
                        }
                    />
                    {isTVPage && <Tabs />}
                </div>
            </div>
        </div>
    );
}

function Tabs() {
    const { menus } = useMenus(pulseConfig);
    const pathname = usePathname();
    const submenus = menus?.getMenuByKey("h-submenus");
    const videoTVTabs = submenus?.find(
        (menu) => menu?.title?.toLowerCase() === "tv"
    );

    return (
        <div className="flex items-center gap-2">
            {videoTVTabs?.subMenus?.map((tab) => {
                const tabActive = pathname.includes(tab.url ?? "");
                return (
                    <Link
                        key={tab.title}
                        className={cn(
                            "border border-white/40 rounded py-1.5 px-6 text-[16px] cursor-pointer hover:bg-primary dark:opacity-65 dark:hover:opacity-100 sm:px-0 sm:py-1",
                            tabActive &&
                                "border-primary bg-primary dark:opacity-100"
                        )}
                        href={tab.url ?? "#"}
                    >
                        <span className="flex items-center justify-center">
                            <span className="text-center dark:text-white sm:text-[8px]">
                                {tab.title}
                            </span>
                        </span>
                    </Link>
                );
            })}
        </div>
    );
}


function CategoriesMenu() {
    const { menus } = useMenus(pulseConfig);
    const submenus = menus?.getMenuByKey("h-submenus");
    const newsCategories = submenus?.find(
        (menu) =>
            menu?.title?.toLowerCase() === "news"
    );

    const pathname = usePathname();

    return (
        <div className="mx-auto mb-4">
            <div
                className={cn(
                    "flex justify-center items-baseline text-sm font-semibold space-x-7 pb-3 border-b-2 border-black/5 dark:border-white/10",
                    "lg:overflow-scroll lg:justify-start lg:space-x-4"
                )}
            >
                <Link
                    href={newsCategories?.url || "#"}
                    key={newsCategories?.title}
                    className={cn(
                        "text-blue hover:text-primary transition-all text-2xl font-bold whitespace-nowrap flex-none lg:text-base dark:text-[#e3e3e3]"
                    )}
                >
                    {newsCategories?.title}
                </Link>
                {newsCategories?.subMenus?.map((item: any, i: any) => (
                    <Link
                        href={item?.url}
                        key={i}
                        className={cn(
                            "font-thin text-blue hover:text-primary transition-all text-xl whitespace-nowrap flex-none lg:text-xs dark:text-[#e3e3e3]",
                            pathname.includes(item?.url) && "font-bold"
                        )}
                    >
                        {item?.title}
                    </Link>
                ))}
            </div>
        </div>
    );
}